import { useCallback, useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { Trash2, Pencil, Shield, UserCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";
import { usePersons } from "@/hooks/usePersons";
import { api, errMsg } from "@/lib/api";
import { PersonForm } from "@/components/PersonForm";
import { ProfileEditor } from "@/components/ProfileEditor";
import { Avatar } from "@/components/PersonCard";
import { years } from "@/lib/tree";
import { toast } from "sonner";

const EditDialog = ({ p, onClose, onSaved }) => (
  <Dialog open onOpenChange={onClose}>
    <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl" data-testid="edit-person-dialog">
      <DialogHeader><DialogTitle className="font-serif">Edit {p.name}</DialogTitle></DialogHeader>
      <ProfileEditor p={p} onSaved={onSaved} testid="edit-person" />
    </DialogContent>
  </Dialog>
);

const PeopleList = ({ persons, user, reload, refresh }) => {
  const [editing, setEditing] = useState(null);
  const del = async (p) => {
    if (!window.confirm(`Remove ${p.name} from the tree?`)) return;
    try { await api.delete(`/persons/${p.person_id}`); toast.success("Removed"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  const claim = async (p) => {
    try { await api.post(`/persons/${p.person_id}/claim`); toast.success(`You are now linked as ${p.name}`); refresh(); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="divide-y divide-[#E5DEC9] rounded-3xl border border-[#E5DEC9] bg-white">
      {persons.length === 0 && <p className="p-8 text-center text-[#7E8E85]">No people yet.</p>}
      {persons.map((p) => (
        <div key={p.person_id} className="flex items-center gap-4 p-4" data-testid={`admin-person-row-${p.person_id}`}>
          <Avatar p={p} size="w-10 h-10 text-sm" />
          <div className="flex-1 min-w-0"><p className="font-medium truncate">{p.name} <span className={`ml-2 text-[10px] px-2 py-0.5 rounded-full ${p.has_account ? "bg-[#E8F5E9] text-[#2E7D32]" : "bg-[#F4EDE2] text-[#7E8E85]"}`}>{p.has_account ? "Claimed" : "Unclaimed"}</span></p><p className="font-mono text-xs text-[#7E8E85]">{years(p)}</p></div>
          {!user.person && !p.has_account && <Button size="sm" variant="outline" className="rounded-full" onClick={() => claim(p)} data-testid={`admin-claim-btn-${p.person_id}`}><UserCircle className="w-4 h-4 mr-1" />This is me</Button>}
          <Button size="icon" variant="ghost" onClick={() => setEditing(p)} data-testid={`admin-edit-btn-${p.person_id}`}><Pencil className="w-4 h-4" /></Button>
          <Button size="icon" variant="ghost" onClick={() => del(p)} className="text-[#C62828]" data-testid={`admin-delete-btn-${p.person_id}`}><Trash2 className="w-4 h-4" /></Button>
        </div>
      ))}
      {editing && <EditDialog p={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); reload(); }} />}
    </div>
  );
};

const UsersList = ({ me }) => {
  const [users, setUsers] = useState([]);
  const load = useCallback(() => api.get("/admin/users").then(({ data }) => setUsers(data)), []);
  useEffect(() => { load(); }, [load]);
  const toggle = async (u) => {
    try { await api.post(`/admin/users/${u.user_id}/toggle-admin`); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="divide-y divide-[#E5DEC9] rounded-3xl border border-[#E5DEC9] bg-white">
      {users.map((u) => (
        <div key={u.user_id} className="flex items-center gap-4 p-4" data-testid={`admin-user-row-${u.user_id}`}>
          <div className="flex-1 min-w-0"><p className="font-medium truncate">{u.name}</p><p className="text-xs text-[#7E8E85] truncate">{u.email}</p></div>
          {u.is_admin && <span className="text-xs px-2.5 py-1 rounded-full bg-[#1E3A2B] text-white flex items-center gap-1"><Shield className="w-3 h-3" />Admin</span>}
          {u.user_id !== me.user_id && <Button size="sm" variant="outline" className="rounded-full" onClick={() => toggle(u)} data-testid={`admin-toggle-btn-${u.user_id}`}>{u.is_admin ? "Remove admin" : "Make admin"}</Button>}
        </div>
      ))}
    </div>
  );
};

export default function Admin() {
  const { user, loading, refresh } = useAuth();
  const { persons, t, reload } = usePersons();
  if (loading) return null;
  if (!user?.is_admin) return <Navigate to="/" replace />;
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-14 space-y-8" data-testid="admin-panel">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#D96B43] mb-3">Admin</p>
        <h1 className="font-serif text-4xl font-bold tracking-tight">Manage the family tree</h1>
        <p className="mt-3 text-[#52635B]">People you add are approved instantly. Real members can later sign in, claim their profile and edit it themselves. Pending entries can be approved on the Approvals page.</p>
      </div>
      <Tabs defaultValue="add">
        <TabsList className="rounded-full bg-[#F4EDE2]">
          <TabsTrigger value="add" className="rounded-full" data-testid="admin-tab-add">Add person</TabsTrigger>
          <TabsTrigger value="people" className="rounded-full" data-testid="admin-tab-people">People ({persons?.length ?? 0})</TabsTrigger>
          <TabsTrigger value="users" className="rounded-full" data-testid="admin-tab-users">Accounts</TabsTrigger>
        </TabsList>
        <TabsContent value="add" className="pt-6">
          {persons && <PersonForm persons={persons} t={t} isSelf={false} onDone={reload} submitLabel="Add to tree" allowNoRelative />}
        </TabsContent>
        <TabsContent value="people" className="pt-6">{persons && <PeopleList persons={persons} user={user} reload={reload} refresh={refresh} />}</TabsContent>
        <TabsContent value="users" className="pt-6"><UsersList me={user} /></TabsContent>
      </Tabs>
    </div>
  );
}
